import React, { useMemo, useState } from "react";
import { X, Upload, Loader2, FileSpreadsheet, ShieldCheck, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * BulkInviteCSVModal — V22.9.
 *
 * Drag-drop / paste-CSV uploader that posts to `POST /api/auth/invite/bulk`.
 * The backend enforces the V22.9 hierarchical role-cascade per row, so this
 * component is only responsible for parsing + presenting per-row results.
 *
 * Expected CSV header (case-insensitive):
 *   email,first_name,last_name,role,department,department_id,process_id,target_org_id
 *
 * Only `email`, `first_name`, `role` are required per row. The available
 * `role` values for the current user are previewable via the modal's "What
 * roles can I invite?" hint (driven by `/auth/invitable-roles`).
 */
const REQUIRED_HEADERS = ["email", "first_name", "role"];
const ALL_HEADERS = [
  "email", "first_name", "last_name", "role",
  "department", "department_id", "process_id", "target_org_id",
  "phone", "location", "home_street", "home_city", "home_state",
  "home_postal_code", "home_country", "department_head",
];

function downloadTemplate() {
  const example = [
    "jane@example.com", "Jane", "Doe", "process_contributor",
    "Operations", "", "", "",
    "555-0100", "HQ", "", "", "", "", "", "",
  ];
  const csv = ALL_HEADERS.join(",") + "\n" + example.join(",") + "\n";
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "bulk_invite_template.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

function parseCsv(text) {
  // Tiny, pragmatic CSV parser — handles quoted fields & commas, no escapes.
  const rows = [];
  let cur = [], field = "", quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], n = text[i + 1];
    if (c === '"' && quoted && n === '"') { field += '"'; i++; continue; }
    if (c === '"') { quoted = !quoted; continue; }
    if (c === "," && !quoted) { cur.push(field); field = ""; continue; }
    if ((c === "\n" || c === "\r") && !quoted) {
      if (field || cur.length) { cur.push(field); rows.push(cur); cur = []; field = ""; }
      if (c === "\r" && n === "\n") i++;
      continue;
    }
    field += c;
  }
  if (field || cur.length) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.some((c) => c && c.trim()));
}

export default function BulkInviteCSVModal({ open, onClose, onInvited }) {
  const { api } = useAuth();
  const [csv, setCsv] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [parseError, setParseError] = useState("");

  const previewRows = useMemo(() => {
    if (!csv.trim()) return null;
    try {
      const grid = parseCsv(csv);
      if (grid.length < 2) return { error: "Need at least one header row + one data row." };
      const headers = grid[0].map((h) => h.trim().toLowerCase());
      for (const req of REQUIRED_HEADERS) {
        if (!headers.includes(req)) return { error: `Missing required column: ${req}` };
      }
      const ignored = headers.filter((h) => h && !ALL_HEADERS.includes(h));
      const rows = grid.slice(1).map((r) => {
        const obj = {};
        headers.forEach((h, i) => {
          if (ALL_HEADERS.includes(h)) obj[h] = (r[i] || "").trim();
        });
        return obj;
      }).filter((r) => r.email && r.first_name && r.role);
      return { headers, rows, ignored };
    } catch (e) {
      return { error: `Could not parse CSV: ${e.message}` };
    }
  }, [csv]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setParseError("CSV must be < 1 MB. Split into smaller batches if larger.");
      return;
    }
    const text = await file.text();
    setCsv(text);
    setParseError("");
    setResult(null);
  };

  const reset = () => {
    setCsv(""); setResult(null); setParseError("");
  };

  const submit = async () => {
    if (!previewRows?.rows?.length) {
      toast.error("No valid rows to invite.");
      return;
    }
    if (previewRows.rows.length > 500) {
      toast.error("Maximum 500 rows per upload. Split into smaller batches.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/invite/bulk", { rows: previewRows.rows });
      setResult(res.data);
      const { invited, skipped, errors } = res.data;
      toast.success(`Bulk invite complete: ${invited} invited · ${skipped} skipped · ${errors} errors`);
      if (invited > 0) onInvited?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="bulk-invite-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Bulk Invite (CSV)</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Up to 500 rows per upload · V22.9 hierarchical cascade enforced per row · 7-day OTP expiry
              </p>
            </div>
          </div>
          <button onClick={() => { reset(); onClose?.(); }} className="text-slate-400 hover:text-slate-700" data-testid="bulk-invite-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* CSV format hint */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
            <p className="font-semibold text-slate-700 mb-1">CSV format</p>
            <code className="block bg-white border border-slate-200 rounded px-2 py-1 text-[11px] text-slate-700 whitespace-normal break-words">
              email,first_name,last_name,role,department,department_id,process_id,target_org_id,phone,location,home_street,home_city,home_state,home_postal_code,home_country,department_head
            </code>
            <p className="mt-1.5">
              <span className="font-semibold">Required:</span> email, first_name, role.
              <span className="font-semibold"> Optional:</span> last_name, department, department_id,
              process_id, target_org_id, phone, location, home_street, home_city, home_state,
              home_postal_code, home_country, department_head.
            </p>
            <p className="mt-1.5">
              The <code>role</code> must be one of the values you can invite per
              the V22.9 cascade (open the single-invite modal to see your dropdown).
              <code>location</code> is an existing site name or <code>work_from_home</code>; the
              <span className="font-semibold"> home address columns (home_street, home_city, home_state,
              home_postal_code, home_country) are used only when location is Work From Home</span>.
              Set <code>department_head</code> to <code>primary</code> or <code>backup</code> (blank = not a
              Department Head; any other non-empty value = primary) to assign the person as that department's head — the one-role
              rule is enforced, and a conflicting person is <span className="text-rose-700 font-medium">blocked</span> with a reason.
              Invalid rows are <span className="text-amber-700 font-medium">flagged, never silently dropped</span>.
            </p>
          </div>

          {/* File picker */}
          <div className="border-2 border-dashed border-slate-300 rounded-md p-5 text-center hover:border-emerald-400 transition-colors">
            <Upload className="w-7 h-7 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600 mb-2">Choose a CSV file or paste below</p>
            <label className="inline-block px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded cursor-pointer">
              Select CSV
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="bulk-invite-file-input"
              />
            </label>
          </div>

          {/* Paste area */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-700">Or paste CSV content</label>
              <button type="button" onClick={downloadTemplate}
                className="text-xs text-emerald-700 underline" data-testid="bulk-invite-download-template">
                Download template
              </button>
            </div>
            <textarea
              rows={6}
              value={csv}
              onChange={(e) => { setCsv(e.target.value); setParseError(""); setResult(null); }}
              placeholder="email,first_name,last_name,role&#10;jane@acme.com,Jane,Doe,department_head&#10;john@acme.com,John,Smith,it_drp_manager"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="bulk-invite-csv-input"
            />
          </div>

          {/* Parse status */}
          {parseError && (
            <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-700">
              {parseError}
            </div>
          )}
          {previewRows?.error && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700">
              {previewRows.error}
            </div>
          )}
          {previewRows?.rows?.length > 0 && !result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-xs text-emerald-800" data-testid="bulk-invite-preview">
              <CheckCircle2 className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
              Parsed <strong>{previewRows.rows.length}</strong> row(s) ready to invite.
            </div>
          )}
          {previewRows?.ignored?.length > 0 && !result && (
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs text-slate-600" data-testid="bulk-invite-ignored">
              Ignored unknown column(s): <strong>{previewRows.ignored.join(", ")}</strong>
            </div>
          )}

          {/* Result panel */}
          {result && (
            <div className="border border-slate-200 rounded-md" data-testid="bulk-invite-results">
              <div className="grid grid-cols-3 text-center text-xs border-b border-slate-200">
                <div className="py-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" /><strong className="text-emerald-700">{result.invited}</strong> invited</div>
                <div className="py-2 border-x border-slate-200"><AlertTriangle className="w-4 h-4 text-amber-500 mx-auto" /><strong className="text-amber-700">{result.skipped}</strong> skipped</div>
                <div className="py-2"><XCircle className="w-4 h-4 text-rose-600 mx-auto" /><strong className="text-rose-700">{result.errors}</strong> errors</div>
              </div>
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left py-1.5 px-2">#</th>
                      <th className="text-left py-1.5 px-2">Email</th>
                      <th className="text-left py-1.5 px-2">Role</th>
                      <th className="text-left py-1.5 px-2">Status</th>
                      <th className="text-left py-1.5 px-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results.map((r) => (
                      <tr key={r.row} className="border-t border-slate-100">
                        <td className="py-1.5 px-2 text-slate-500">{r.row}</td>
                        <td className="py-1.5 px-2 font-mono">{r.email}</td>
                        <td className="py-1.5 px-2">{r.role}</td>
                        <td className="py-1.5 px-2">
                          {r.status === "invited" && <span className="text-emerald-700 font-semibold">✓ invited</span>}
                          {r.status === "skipped" && <span className="text-amber-700 font-semibold">↷ skipped</span>}
                          {r.status === "blocked" && <span className="text-rose-700 font-semibold" data-testid={`bulk-row-blocked-${r.row}`}>⛔ blocked</span>}
                          {r.status === "error" && <span className="text-rose-700 font-semibold">✕ error</span>}
                        </td>
                        <td className="py-1.5 px-2 text-slate-500">{r.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Cascade-validated · Idempotent on re-upload
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { reset(); onClose?.(); }}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
                data-testid="bulk-invite-cancel-btn"
              >
                Close
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !previewRows?.rows?.length || result?.invited > 0}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-1.5 rounded-md flex items-center gap-2"
                data-testid="bulk-invite-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {submitting ? "Sending…" : "Send all invitations"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
